import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import WelcomePopup from '../components/WelcomePopup';

// Use dynamic API URL that works on both dev and production
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return window.location.origin;
};
const API = `${getApiUrl()}/api`;

const AuthCallback = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Processing...');
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [welcomeData, setWelcomeData] = useState({ userNumber: 1, userName: '', user: null });

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Get session_id from URL hash or search params
        let hash = window.location.hash || location.hash || '';
        const urlParams = new URLSearchParams(window.location.search);
        const searchSessionId = urlParams.get('session_id');
        
        console.log('AuthCallback - URL:', window.location.href);
        console.log('AuthCallback - Hash:', hash);

        let sessionId = null;
        if (hash && hash.includes('session_id=')) {
          sessionId = hash.split('session_id=')[1]?.split('&')[0];
        } else if (searchSessionId) {
          sessionId = searchSessionId;
        }
        
        console.log('AuthCallback - Session ID:', sessionId);

        if (!sessionId) {
          setStatus('No session ID found');
          toast.error('Authentication failed - please try again');
          setTimeout(() => navigate('/login', { replace: true }), 1500);
          return;
        }

        setStatus('Verifying with Google...');
        
        // Use backend proxy to exchange session (avoids CORS issues)
        console.log('AuthCallback - Calling backend exchange endpoint...');
        const response = await axios.post(`${API}/auth/emergent/exchange`, {
          session_id: sessionId
        });
        
        console.log('AuthCallback - Response:', response.data);

        if (!response.data || !response.data.user) {
          throw new Error('Failed to authenticate');
        }

        const { user, is_new_user, user_number } = response.data;

        // Show welcome popup for new users
        if (is_new_user) {
          setWelcomeData({ 
            userNumber: user_number || 1, 
            userName: user.name || '', 
            user: user 
          });
          setShowWelcomePopup(true);
          setStatus('Welcome!');
          return; // Don't navigate yet, wait for popup to close
        }

        toast.success('Welcome back! 🎉');
        onLogin(user);

        // Check for pending group join
        const pendingJoin = localStorage.getItem('pendingJoin');
        if (pendingJoin) {
          try {
            const { groupId, inviteCode } = JSON.parse(pendingJoin);
            localStorage.removeItem('pendingJoin');
            await axios.post(`${API}/groups/${groupId}/join?user_id=${user.id}&invite_code=${inviteCode}`);
            navigate(`/group/${groupId}`, { replace: true });
            return;
          } catch (e) {
            console.error('Failed to join group:', e);
          }
        }

        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        console.error('Error details:', error.response?.data);
        
        let errorMessage = 'Authentication failed';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        
        setStatus(`Error: ${errorMessage}`);
        toast.error(errorMessage);
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };

    processAuth();
  }, [navigate, onLogin, location]);

  const handleWelcomePopupClose = async () => {
    setShowWelcomePopup(false);
    const { user } = welcomeData;
    if (user) {
      onLogin(user);
      
      // Check for pending group join
      const pendingJoin = localStorage.getItem('pendingJoin');
      if (pendingJoin) {
        try {
          const { groupId, inviteCode } = JSON.parse(pendingJoin);
          localStorage.removeItem('pendingJoin');
          await axios.post(`${API}/groups/${groupId}/join?user_id=${user.id}&invite_code=${inviteCode}`);
          navigate(`/group/${groupId}`, { replace: true });
          return;
        } catch (e) {
          console.error('Failed to join group:', e);
        }
      }
      
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center">
      {/* Welcome Popup for new users */}
      <WelcomePopup
        isOpen={showWelcomePopup}
        onClose={handleWelcomePopupClose}
        userNumber={welcomeData.userNumber}
        userName={welcomeData.userName}
      />
      
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
